import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

/**
 * Custom hook for toast notifications with i18n support
 * Provides consistent toast patterns across the application
 */
export const useToast = () => {
  const { t } = useTranslation();

  /**
   * Show a toast for a promise
   * Automatically handles loading, success, and error states
   */
  const toastPromise = <T>(
    promise: Promise<T>,
    options: {
      loading: string;
      success: string | ((result: T) => string);
      error?: string | ((error: unknown) => string);
      onSuccess?: (result: T) => void;
      onError?: (error: unknown) => void;
      onFinally?: () => void;
    }
  ): Promise<T> => {
    return toast
      .promise(promise, {
        loading: options.loading,
        success: (result: T) => {
          const message =
            typeof options.success === "function"
              ? options.success(result)
              : options.success;
          options.onSuccess?.(result);
          return message;
        },
        error: (error: unknown) => {
          const message = options.error
            ? typeof options.error === "function"
              ? options.error(error)
              : options.error
            : t("error") || "An error occurred";
          console.error("Toast promise error:", error);
          options.onError?.(error);
          return message;
        },
      })
      .finally(() => {
        options.onFinally?.();
      }) as Promise<T>;
  };

  /**
   * Show success toast
   */
  const showSuccess = (message: string) => {
    toast.success(message);
  };

  /**
   * Show error toast
   */
  const showError = (message: string) => {
    toast.error(message);
  };

  /**
   * Show loading toast
   */
  const showLoading = (message: string) => {
    return toast.loading(message);
  };

  return {
    toastPromise,
    showSuccess,
    showError,
    showLoading,
  };
};
